import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserAuth } from "../context/userAuthContext";
import { Heart, ArrowRight, AlertCircle } from "lucide-react";
import GoogleIcon from "../assets/google-icon.png";

const Signup = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { signUp, googleSignIn } = useUserAuth();
  const navigate = useNavigate();

  const validatePassword = () => {
    if (password !== confirmPassword) {
      setError("Las contraseñas no coinciden");
      return false;
    }
    if (password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!validatePassword()) {
      setIsLoading(false);
      return;
    }

    try {
      await signUp(email, password);
      navigate("/");
    } catch (err) {
      setError("Error al registrar. Verifica tu correo o intenta nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      await googleSignIn();
      navigate("/");
    } catch (error) {
      setError("Error al registrarse con Google. Intenta nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12 font-sans">
      <div className="w-full md:max-w-md">
        {/* Signup Form */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900 text-center">
            Crea tu cuenta en Pollo Feliz
          </h2>

          {/* Error Alert */}
          {error && (
            <div className="bg-red-100 border border-[#FF0000] p-4 mb-6 rounded-xl flex items-start">
              <AlertCircle
                className="h-5 w-5 text-[#FF0000] mr-2 mt-0.5 flex-shrink-0"
                strokeWidth={2.5}
              />
              <div>
                <p className="font-bold text-[#FF0000] text-sm uppercase tracking-wide">
                  Error
                </p>
                <p className="text-sm text-gray-600">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Correo Electrónico
              </label>
              <input
                type="email"
                placeholder="correo@pollofeliz.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF0000] focus:border-[#FF0000] outline-none text-gray-900 placeholder-gray-400 bg-white hover:bg-gray-50 transition-all"
                required
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Contraseña
              </label>
              <input
                type="password"
                placeholder="Mínimo 6 caracteres"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF0000] focus:border-[#FF0000] outline-none text-gray-900 placeholder-gray-400 bg-white hover:bg-gray-50 transition-all"
                required
                minLength={6}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700">
                Confirmar Contraseña
              </label>
              <input
                type="password"
                placeholder="Repite tu contraseña"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF0000] focus:border-[#FF0000] outline-none text-gray-900 placeholder-gray-400 bg-white hover:bg-gray-50 transition-all"
                required
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isLoading}
              className={`w-full py-3 px-4 bg-[#FF0000] text-white rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-all ${
                isLoading
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-[#E60000] hover:shadow-md active:scale-95"
              }`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Creando cuenta...
                </>
              ) : (
                <>
                  <ArrowRight className="w-5 h-5" strokeWidth={2.5} />
                  Crear Cuenta
                </>
              )}
            </button>
          </div>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="px-3 bg-gray-50 text-gray-500 text-sm font-medium">
                O regístrate con
              </span>
            </div>
          </div>

          {/* Google Sign In */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className={`w-full py-3 px-4 bg-white border border-gray-300 text-gray-900 rounded-xl font-bold text-base flex items-center justify-center gap-2 transition-all ${
              isLoading
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50 hover:border-[#FF0000] hover:text-[#FF0000] active:scale-95"
            }`}
          >
            <img src={GoogleIcon} alt="Google" className="w-5 h-5 mr-2" />
            Registrarse con Google
          </button>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-gray-600 text-sm">
              ¿Ya tienes una cuenta?{" "}
              <Link
                to="/login"
                className="font-bold text-[#FF0000] hover:text-[#E60000] transition-colors"
              >
                Inicia Sesión
              </Link>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-500 text-sm font-medium">
            © 2025 Pollo Feliz • Todos los derechos reservados
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;
